import { LitElement, html, css, PropertyValues } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { TerminalsModel } from "../../../src/models/terminals-model";

import '../../voy-terminal-input/src/voy-terminal-input';
import { FetchStatusCodeError } from '../../../src/utils/auth-fetch';

interface PortMemo {
    id?: string;
    content: string;
}

@customElement('port-memo-popup')
export class PortMemoPopup extends LitElement
{
    @property({type: Boolean, reflect: true}) shown: boolean = false;
    @property({type: Number}) selectedPortMemo: number = -1;
    @property({type: String, attribute: "terminal-code"}) terminalCode: string = "";
    @property({type: Array}) portMemos: PortMemo[] = [];
    @property({type: String}) fetchError: string = "";

    readonly notFoundErrorMessage: string = `Selected Terminal does not have any port memo.\n\nThere are no port memo available for selected terminal`;

    static get styles() {
        return [
            css`
            :host {
                font-family: MaerskText;
            }
            
            #port-memo-popup .popup {
                width: 800px;
                max-width: 80vw;
                height: 80vh;
                padding:0 24px;
            }

            .port-memo .error {
                margin: 0;
                padding: 20px;
                white-space: pre-wrap;
            }

            .downloading {
                font-family: MaerskText;
                font-size: 14px;
            }
            
            #topContainer {
                display: grid;
                padding: 10px 24px;
                grid-template-columns: 1fr;
                grid-template-rows:1fr 1fr 1fr;
                align-items: center;
                justify-items: start;
                column-gap: 15px;
                row-gap: 16px;
                border-bottom: 1px solid var(--neutrals-grey-07);
            }
            
            .name {
                color: var(--neutrals-grey-02, grey);
                font-size: 14px;
                font-family: MaerskText;
                line-height:20px;
                padding-bottom: 4px;
            }
            
            .dropdown {
                box-sizing: border-box;
                height: 41px;
                border: 1px solid var(--neutrals-grey-06, grey);
                border-radius: 2.5px;
                font-family: MaerskText;
                font-size: 14px;
                color: var(--neutrals-grey-02, black);
                background-color: var(--neutrals-grey-10, white);
                text-indent: 5px;
                width: 100%;
            }

            .dropdown[disabled] {
                background-color: var(--neutrals-grey-08, black);
            }

            #port-memo-popup #footer {
                width: 100%;
                display: flex;
                justify-content: flex-end;
                border-top: 1px solid var(--neutrals-grey-07);
                padding: 12px;
            }
            
            #port-memo-popup #footer button {
                height: 32px;
                min-width: 80px;
                font-size: 14px;
                font-family: MaerskText;
                background: none;
                border: none;
                outline: none;
                cursor: pointer;
                border-radius: 3px;
                width: auto;
            }

            #port-memo-popup #footer .download-button[disabled] {
                background: var(--neutrals-grey-08, black);
                cursor: auto;
            }

            #port-memo-popup #footer .download-button {
                color: var(--neutrals-grey-02);
                margin-right: 10px;
                border: 1px solid var(--neutrals-grey-06, grey);
                display: flex;
                line-height: 28px;
            }

            #port-memo-popup #footer .download-button > svg {
                margin: 5px 8px 0 0;
                height: 20px;
                width: 20px;
            }
            
            #port-memo-popup #footer .close-button {
                background: var(--primary-blue-05, blue);
                margin-right: 20px;
                color: var(--neutrals-grey-10, white);
            }

            voy-terminal-input {
                margin-bottom: 5px;
            }

            .gsis-input{
                width: 226px;
                position: realtive;
            }

            .max-width-container {
                width: 500px;
            }
            .center {
                display: flex;
                justify-content: center;
                align-items: center;
                height: 100%;
            }
        `];
    }

    render()
    {
        return html`
        <voyage-popup id="port-memo-popup" header-text="${this._extractPort(this.terminalCode)} Port Memo" 
                          ?shown=${this.shown} @close=${() => this._close()}>
            <div id="topContainer">
                <div class="max-width-container">
                    <div class="name">Terminal</div>
                    <voy-terminal-input .terminalId=${this.terminalCode} .showTerminalsCode=${false}
                        @terminal-selected=${(evt: CustomEvent) => { this.terminalCode = evt.detail.locationId }}>
                    </voy-terminal-input>
                </div>
                <div class="max-width-container">
                    <div class="name">Berth</div>
                    <select class="dropdown" 
                        .value=${this.selectedPortMemo.toString()} 
                        @input=${(evt: { target: { value: number; }; }) => this.selectedPortMemo = evt.target.value }
                        ?disabled=${this.portMemos.length == 0} >
                        <option value="-1">Select...</option>
                        ${this.portMemos.map( (portMemo, index) => html`
                        <option value=${index} ?selected=${this.selectedPortMemo === index}>${portMemo.content.split("\n")[1]}</option>
                        `)}
                    </select>
                </div>
                <div class="gsis-input">
                    <div class="name">GSIS terminal code</div>
                        <voy-terminal-input .terminalId=${this.terminalCode} .showTerminalsCode=${true} .placeholderText="Search GSIS"
                            @terminal-selected=${(evt: CustomEvent) => { this.terminalCode = evt.detail.locationId }}>
                        </voy-terminal-input>
                    </div>
                </div>
            </div>
            <div class="popup">
                ${this._downloading ? html`<div class="downloading">Downloading...</div>` : html``}
                ${this.fetchError !== "" ? html`<pre class="error">${this.fetchError}</pre>` : html``}
                ${this.selectedPortMemo > -1 ? html`<pre class="port-memo">${this.portMemos[this.selectedPortMemo].content}</pre>` : html`<div class="center">Please select a Port Memo from the "Berth" list above</div>`}
            </div>
            <div id="footer" slot="footer">
                <button class="download-button" ?disabled=${this.selectedPortMemo === -1} @click=${() => this._onDownloadPortMemo()}>
                    ${SvgIcons.solidPrint} Download Port Memo
                </button>
                <button class="close-button" @click=${() => this._close()} >Close</button>
            </div>
        </voyage-popup>
        `;
    }

    updated(changedProperties: PropertyValues) {
        changedProperties.forEach((oldValue, propName) => {
            if (propName === "terminalCode") {
                this.portMemos = [];
                this.selectedPortMemo = -1;
                this.fetchError = "";
                this._downloading = true;
                this._fetchPortMemo();
            }
            if (propName === "shown" && this.shown) {
                const popup = this.shadowRoot!.querySelector("voyage-popup");

                if (popup) {
                    popup.scrollTo(0, 0);
                }
            }
        });
    }

    @property({type: Boolean}) private _downloading: boolean = false;

    private _close() {
        this.dispatchEvent(new Event("close"));
    }

    private async _fetchPortMemo() {
        if (!this.terminalCode) {
            this._downloading = false;
            return;
        }

        try {
            this.portMemos = await TerminalsModel!.getTerminalPortMemo(this.terminalCode);
            // Select the first portMemo if we only have one.
            if (this.portMemos.length === 1) {
                this.selectedPortMemo = 0;
            }
        } catch(err) {
            if (err instanceof FetchStatusCodeError)
            {
                if (err.response.status === 404)
                {
                    this.fetchError = this.notFoundErrorMessage
                }
                else
                {
                    this.fetchError = `Something went wrong. Developer error message: ${err.message}`
                }
            }
            else
            {
                this.fetchError = `Something went wrong. Developer error message: ${err.message}`
            }
        }

        this._downloading = false;
    }

    private _onDownloadPortMemo() {
        if (this.selectedPortMemo == -1) {
            return;
        }

        let a = window.document.createElement('a');

        // If we are on Windows, ensure all line endings are CR+LF
        let content = this.portMemos[this.selectedPortMemo].content + '\n';
        if (window && window.navigator && ['Win32', 'Win64', 'Windows', 'WinCE'].indexOf(window.navigator.platform) !== -1) {
            content = content.split('\r').join('').split('\n').join('\r\n');
        }

        a.href = window.URL.createObjectURL(new Blob([content], {type: 'text/plain'}));
        // file name is TERMINAL_ID.txt, for example: RUPLPTM.txt
        a.download = this.terminalCode.toUpperCase() + '.txt';

        // Append anchor to body.
        document.body.appendChild(a);
        a.click();

        // Remove anchor from body
        document.body.removeChild(a);
    }

    private _extractPort(terminalCode: string) {
        if (terminalCode === ""){
            return "";
        } 

        return TerminalsModel!.getCity(terminalCode) || terminalCode;
    }
}
